import React from 'react'
import { Link2, ScrollText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { windowPonyfill } from '@/lib/utils/window-ponyfill'

type TooltipSide = 'top' | 'left' | 'right' | 'bottom'

interface MessageActionsMenuProps {
  threadId: string
  messageId: string
  companyId?: string
  className?: string
  sessionId?: string
  onSessionDetailsClick?: (sessionId: string) => void
  /** 'row' = buttons side by side (for single-line events), 'col' = stacked (for cards). Default: 'col' */
  layout?: 'row' | 'col'
  /** Which side the tooltip appears on. Default: 'top' */
  tooltipSide?: TooltipSide
}

export const MessageActionsMenu: React.FC<MessageActionsMenuProps> = ({
  threadId,
  messageId,
  companyId,
  className,
  sessionId,
  onSessionDetailsClick,
  layout = 'col',
  tooltipSide = 'top',
}) => {
  const generateLink = () => {
    const baseUrl = windowPonyfill.location.origin
    const params = new URLSearchParams({
      threadId,
      messageId,
    })
    if (companyId) {
      params.set('companyId', companyId)
    }
    return `${baseUrl}/dashboard/activity?${params.toString()}`
  }

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const link = generateLink()
      await windowPonyfill.navigator.clipboard.writeText(link)
      toast.success('Link copied to clipboard')
    } catch (error) {
      console.error('Failed to copy link:', error)
      toast.error('Failed to copy link')
    }
  }

  const buttonClass = cn(
    'opacity-0 group-hover/message:opacity-100 group-hover:opacity-100 group-hover/card:opacity-100 transition-opacity duration-150',
    'p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-muted bg-white dark:bg-background text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground',
    'flex items-center justify-center z-30',
    'shadow-sm border border-gray-200 dark:border-border',
    'min-w-[24px] min-h-[24px]',
    className,
  )

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className={`flex ${layout === 'row' ? 'flex-row' : 'flex-col'} gap-1`}
      >
        {onSessionDetailsClick && sessionId && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className={buttonClass}
                onClick={e => {
                  e.stopPropagation()
                  onSessionDetailsClick(sessionId)
                }}
                aria-label="View session details"
              >
                <ScrollText className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide} className="text-xs">
              View session details
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={buttonClass}
              onClick={handleCopyLink}
              aria-label="Copy link"
            >
              <Link2 className="w-4 h-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide} className="text-xs">
            Copy link
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  )
}
